import type { FeatureFlagKey } from '@erp/shared-types';
import type { AppRouteMeta } from '@/routing/route-meta';
import type { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { getPermissionKey } from '@/hooks/authz/useRoutePermissions';

export type AccessContext = {
  // who
  isAdmin: boolean;

  // feature flags
  isFeatureEnabled: (flag: FeatureFlagKey) => boolean;

  // module enablement
  isModuleEnabled: (module: ProductModule) => boolean;

  // permissions cache
  permissions: Map<string, boolean>;
  permissionsLoading: boolean;
};

// fallow-ignore-next-line complexity
export function canAccessAppRouteMeta(
  meta: AppRouteMeta | undefined,
  ctx: AccessContext,
): boolean {
  const access = meta?.access;
  const nav = meta?.nav;

  if (nav?.hidden) return false;

  if (access?.adminOnly && !ctx.isAdmin) return false;

  if (
    access?.adminOrPermission &&
    !ctx.isAdmin &&
    // Allow while permissions are loading to avoid nav flash.
    !ctx.permissionsLoading &&
    !ctx.permissions.get(
      getPermissionKey(
        access.adminOrPermission.action,
        access.adminOrPermission.subject,
      ),
    )
  ) {
    return false;
  }

  if (access?.featureFlag && !ctx.isFeatureEnabled(access.featureFlag)) {
    return false;
  }
  if (
    access?.hiddenWhenFeatureFlag &&
    ctx.isFeatureEnabled(access.hiddenWhenFeatureFlag)
  ) {
    return false;
  }

  const anyOfModules = access?.modulesAnyOf ?? [];
  const allOfModules = access?.modulesAllOf ?? [];

  const modulesAnyOk =
    anyOfModules.length === 0 ||
    anyOfModules.some((m) => ctx.isModuleEnabled(m as any));
  const modulesAllOk =
    allOfModules.length === 0 ||
    allOfModules.every((m) => ctx.isModuleEnabled(m as any));

  if (!modulesAnyOk || !modulesAllOk) return false;

  // Allow all while permissions are loading to avoid nav flash.
  if (ctx.permissionsLoading) return true;

  const anyOfPerms = access?.permissionsAnyOf ?? [];
  const allOfPerms = access?.permissionsAllOf ?? [];

  const permsAnyOk =
    anyOfPerms.length === 0 ||
    anyOfPerms.some((p) =>
      ctx.permissions.get(getPermissionKey(p.action, p.subject)),
    );
  const permsAllOk =
    allOfPerms.length === 0 ||
    allOfPerms.every((p) =>
      ctx.permissions.get(getPermissionKey(p.action, p.subject)),
    );

  if (!permsAnyOk || !permsAllOk) return false;

  return true;
}
